import { io } from 'socket.io-client';
import API_CONFIG from '../config/api';

class SocketService {
  constructor() {
    this.socket = null;
    this.connected = false;
    this.token = null;
  }

  connect(token) {
    // 单例复用：同一 token 下已连接或正在连接/重连的 socket 直接复用。
    // 旧逻辑只在 connected 时复用，否则会另建连接，页面拿到的仍是旧实例。
    if (this.socket && this.token === token && (this.socket.connected || this.socket.active)) {
      return this.socket;
    }

    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    this.token = token;
    this.connected = false;

    this.socket = io(API_CONFIG.SOCKET_URL, {
      auth: {
        token
      },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      // 不再 5 次后彻底放弃：网络抖动/后端重启时能一直恢复
      reconnectionAttempts: Infinity,
      timeout: 10000
    });

    this.socket.on('connect', () => {
      console.log('Socket connected');
      this.connected = true;
    });

    this.socket.on('disconnect', () => {
      console.log('Socket disconnected');
      this.connected = false;
    });

    this.socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error?.message || error);
      this.connected = false;
    });

    return this.socket;
  }

  isConnected() {
    return Boolean(this.socket?.connected);
  }

  // 订阅连接状态：若已连接立即回调，避免错过已经发生的 connect 事件
  onConnect(callback) {
    if (!this.socket) return () => {};
    if (this.socket.connected) {
      callback();
      return () => {};
    }
    this.socket.on('connect', callback);
    return () => this.socket?.off('connect', callback);
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connected = false;
      this.token = null;
    }
  }

  // Gửi tin nhắn
  sendMessage(message, receiverId, callback, imageUrl = null) {
    if (!this.socket) return;
    this.socket.emit('send-message', { message, receiverId, imageUrl }, callback);
  }

  // Lắng nghe tin nhắn mới
  onNewMessage(callback) {
    if (!this.socket) return;
    this.socket.on('new-message', callback);
  }

  offNewMessage(callback) {
    if (!this.socket) return;
    this.socket.off('new-message', callback);
  }

  // Lấy danh sách user đã chat (admin only)
  getChatUsers(callback) {
    if (!this.socket) return;
    this.socket.emit('get-chat-users', callback);
  }

  // 获取自己的客服未读数（user 端）
  getMyUnread(callback) {
    if (!this.socket) return;
    this.socket.emit('get-my-unread', callback);
  }

  // Đánh dấu đã đọc
  markAsRead(userId) {
    if (!this.socket) return;
    this.socket.emit('mark-as-read', { userId });
  }

  // Typing indicators
  startTyping(userId) {
    if (!this.socket) return;
    this.socket.emit('typing', { userId });
  }

  stopTyping(userId) {
    if (!this.socket) return;
    this.socket.emit('stop-typing', { userId });
  }

  onUserTyping(callback) {
    if (!this.socket) return;
    this.socket.on('user-typing', callback);
  }

  offUserTyping(callback) {
    if (!this.socket) return;
    this.socket.off('user-typing', callback);
  }

  onUserStopTyping(callback) {
    if (!this.socket) return;
    this.socket.on('user-stop-typing', callback);
  }

  offUserStopTyping(callback) {
    if (!this.socket) return;
    this.socket.off('user-stop-typing', callback);
  }

  // Remove listeners
  removeAllListeners() {
    if (!this.socket) return;
    this.socket.removeAllListeners();
  }
}

export default new SocketService();
